import React, { useState, useRef, useMemo, useEffect } from "react";
import "./location.css";
import {
  StandaloneSearchBox,
  GoogleMap,
  MarkerF,
} from "@react-google-maps/api";
import api from "../../api/api";
import * as newApi from "../../api/apiCollection";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiCurrentLocation } from "react-icons/bi";
import { IoLocationOutline, IoHome } from "react-icons/io5";
import { MdHome, MdLocationOff, MdWorkOutline } from "react-icons/md";
import { toast } from "react-toastify";
import Loader from "../loader/Loader";
import { useTranslation } from "react-i18next";
import { setCity } from "../../model/reducer/locationReducer";
import { setSetting } from "../../model/reducer/settingReducer";
import { setShop } from "../../model/reducer/shopReducer";
import { setSelectedAddress } from "../../model/reducer/addressReducer";

const Location = (props) => {
  const dispatch = useDispatch();
  const setting = useSelector((state) => state.setting);
  const user = useSelector((state) => state.user);
  const addresses = useSelector((state) => state.address?.address);
  const selectedAddress = useSelector(
    (state) => state.address?.selected_address
  );

  const [isloading, setisloading] = useState(false);
  const [currLocationClick, setcurrLocationClick] = useState(false);
  const [isInputFields, setisInputFields] = useState(false);
  const [errorMsg, seterrorMsg] = useState("");
  const [isAddressLoading, setisAddressLoading] = useState(false);
  const [locationPermission, setLocationPermission] = useState("prompt"); // 'granted', 'denied', 'prompt'
  const [localLocation, setlocalLocation] = useState({
    city: "",
    formatted_address: "",
    lat: parseFloat(0),
    lng: parseFloat(0),
  });

  const center = useMemo(
    () => ({
      lat: localLocation.lat,
      lng: localLocation.lng,
      streetViewControl: false,
    }),
    [localLocation.lat, localLocation.lng]
  );

  const inputRef = useRef();
  const { t } = useTranslation();

  useEffect(() => {
    checkLocationPermission();
  }, []);

  useEffect(() => {
    if (
      selectedAddress &&
      selectedAddress.latitude &&
      selectedAddress.longitude
    ) {
      fetchCity(
        selectedAddress.city,
        selectedAddress.latitude,
        selectedAddress.longitude
      )
        .then((result) => {
          if (result.status === 1) {
            dispatch(
              setCity({
                data: {
                  id: result.data.id,
                  name: selectedAddress.city,
                  state: result.data.state,
                  formatted_address: `${selectedAddress.city}, ${selectedAddress.state}, ${selectedAddress.country}`,
                  latitude: result.data.latitude,
                  longitude: result.data.longitude,
                  min_amount_for_free_delivery:
                    result.data.min_amount_for_free_delivery,
                  delivery_charge_method: result.data.delivery_charge_method,
                  fixed_charge: result.data.fixed_charge,
                  per_km_charge: result.data.per_km_charge,
                  time_to_travel: result.data.time_to_travel,
                  max_deliverable_distance:
                    result.data.max_deliverable_distance,
                  distance: result.data.distance,
                },
              })
            );
            fetchShop(result.data.latitude, result.data.longitude);
          }
        })
        .catch((error) =>
          console.log("Error fetching city for selected address:", error)
        );
    }
  }, [selectedAddress]);

  // Check location permission
  const checkLocationPermission = () => {
    if (!("geolocation" in navigator)) {
      setLocationPermission("denied");
      if (props?.setShowPermissionDenied) {
        props.setShowPermissionDenied(true);
      }
      return;
    }

    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((permissionStatus) => {
          setLocationPermission(permissionStatus.state);
          if (
            permissionStatus.state === "prompt" ||
            permissionStatus.state === "denied"
          ) {
            if (props?.setShowPermissionDenied) {
              props.setShowPermissionDenied(true);
            }
          } else {
            if (props?.setShowPermissionDenied) {
              props.setShowPermissionDenied(false);
            }
          }

          // Listen for permission changes
          permissionStatus.onchange = () => {
            setLocationPermission(permissionStatus.state);
            if (permissionStatus.state === "granted") {
              if (props?.setShowPermissionDenied) {
                props.setShowPermissionDenied(false);
              }
              // Automatically get current location when permission is granted
              handleCurrentLocationClick();
            } else if (permissionStatus.state === "denied") {
              if (props?.setShowPermissionDenied) {
                props.setShowPermissionDenied(true);
              }
            }
          };
        });
    }
  };

  // Request permission for location
  const requestLocationPermission = () => {
    if (!("geolocation" in navigator)) {
      toast.error(t("Geolocation not supported"));
      return;
    }

    setisloading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationPermission("granted");
        if (props?.setShowPermissionDenied) {
          props?.setShowPermissionDenied(false);
        }
        if (!currLocationClick) {
          setcurrLocationClick(false);
        }
        setisloading(false);
        handleCurrentLocationClick();
      },
      (error) => {
        setLocationPermission("denied");
        if (props?.setShowPermissionDenied) {
          props?.setShowPermissionDenied(true);
        }
        setisloading(false);
        setcurrLocationClick(false);
        toast.error(t("Location permission denied"));
      },
      { enableHighAccuracy: true }
    );
  };

  // By Selecting place from input field
  const handlePlaceChanged = () => {
    setisloading(true);

    const [place] = inputRef.current.getPlaces();
    try {
      if (place) {
        let city_name = place.address_components[0].long_name;
        let loc_lat = place.geometry.location.lat();
        let loc_lng = place.geometry.location.lng();
        let formatted_address = place.formatted_address;
        fetchCity(city_name, loc_lat, loc_lng)
          .then((res) => {
            if (res.status === 1) {
              dispatch(
                setCity({
                  data: {
                    id: res.data.id,
                    name: city_name,
                    state: res.data.state,
                    formatted_address: formatted_address,
                    latitude: res.data.latitude,
                    longitude: res.data.longitude,
                    min_amount_for_free_delivery:
                      res.data.min_amount_for_free_delivery,
                    delivery_charge_method: res.data.delivery_charge_method,
                    fixed_charge: res.data.fixed_charge,
                    per_km_charge: res.data.per_km_charge,
                    time_to_travel: res.data.time_to_travel,
                    max_deliverable_distance: res.data.max_deliverable_distance,
                    distance: res.data.distance,
                  },
                })
              );
              const updatedSetting = {
                ...setting?.setting,
                default_city: {
                  id: res?.data?.id,
                  name: city_name,
                  state: res?.data?.name,
                  formatted_address: formatted_address,
                  latitude: res?.data?.latitude,
                  longitude: res?.data?.longitude,
                },
              };
              dispatch(setSetting({ data: updatedSetting }));
              fetchShop(res.data.latitude, res.data.longitude);
              setisloading(false);
              props.setLocModal(false);
              props.bodyScroll(false);
              props.setisLocationPresent(true);
              props.setLocModal(false);
              props.setIsAddressSelected(false);
            } else if (res.status == 0) {
              setisloading(false);
              toast.error(t("We doesn't deliver at selected city"));
              props.setisLocationPresent(false);
              props.setLocModal(true);
            } else {
              setisloading(false);
              seterrorMsg(res.message);
            }
          })
          .catch((error) => {
            console.log(error);
          });
        props.setisLocationPresent(true);
      } else {
        props.setLocModal(true);
        setisloading(false);
      }
    } catch (e) {
      toast.error("Location not found!");
    }
    setisloading(false);
  };

  //fetching city from server
  const fetchCity = async (city_name, loc_lat, loc_lng) => {
    const response = await api.getCity(loc_lat, loc_lng);
    const res = await response.json();
    return res;
  };

  //Select Current Location
  const handleCurrentLocationClick = () => {
    setisloading(true);
    setisInputFields(false);
    setcurrLocationClick(true);

    if (!("geolocation" in navigator)) {
      onError({
        code: 0,
        message: "Geolocation not supported",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(onSuccess, onError);
  };

  // Handle location button click
  const handleLocationButtonClick = () => {
    if (locationPermission === "granted") {
      handleCurrentLocationClick();
    } else {
      requestLocationPermission();
      // Show permission denied message if permission is denied
      if (locationPermission === "denied") {
        props?.setShowPermissionDenied(true);
      }
    }
  };

  const onSuccess = (location) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder
      .geocode({
        location: {
          lat: location.coords.latitude,
          lng: location.coords.longitude,
        },
      })
      .then((response) => {
        if (response.results[0]) {
          //get city
          getAvailableCity(response)
            .then((res) => {
              if (res.status === 1) {
                setlocalLocation({
                  city: res.data.name,
                  formatted_address: res.data.formatted_address,
                  lat: parseFloat(response.results[0].geometry.location.lat()),
                  lng: parseFloat(response.results[0].geometry.location.lng()),
                });
              } else {
                seterrorMsg(res.message);
                setlocalLocation({
                  city: response.results[0].address_components[0].long_name,
                  formatted_address: response.results[0].formatted_address,
                  lat: parseFloat(response.results[0].geometry.location.lat()),
                  lng: parseFloat(response.results[0].geometry.location.lng()),
                });
              }
            })
            .catch((error) => console.log("error " + error));
          setisloading(false);
        } else {
          console.log("No result found");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const onError = (error) => {
    console.log(error);
    setisloading(false);
    if (error.code === 1) {
      setLocationPermission("denied");
      props.setShowPermissionDenied(true);
      toast.error(t("Location permission denied"));
    } else {
      toast.error(t("Unable to get location"));
    }
    setcurrLocationClick(false);
  };

  //get available delivery location city
  const getAvailableCity = async (response) => {
    var results = response.results;
    var c, lc, component;
    var found = false,
      message = "";
    for (var r = 0, rl = results.length; r < 2; r += 1) {
      var flag = false;
      var result = results[r];
      for (c = 0, lc = result.address_components.length; c < 2; c += 1) {
        component = result.address_components[c];

        //confirm city from server
        const response = await api
          .getCity(
            result.geometry.location.lat(),
            result.geometry.location.lng()
          )
          .catch((error) => console.log("error: ", error));
        const res = await response.json();
        if (res.status === 1) {
          flag = true;
          found = true;
          return res;
        } else {
          found = false;
          message = res.message;
        }
        if (flag === true) {
          break;
        }
      }
      if (flag === true) {
        break;
      }
    }
    if (found === false) {
      return {
        status: 0,
        message: message,
      };
    }
  };

  const onMarkerDragStart = () => {
    setisAddressLoading(true);
  };

  const onMarkerDragEnd = (e) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder
      .geocode({
        location: {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        },
      })
      .then((response) => {
        if (response.results[0]) {
          //get city
          getAvailableCity(response)
            .then((res) => {
              if (res.status === 1) {
                setlocalLocation({
                  city: res.data.name,
                  formatted_address: response.results[0].formatted_address,
                  lat: response.results[0].geometry.location.lat(),
                  lng: response.results[0].geometry.location.lng(),
                });
                setisAddressLoading(false);
                seterrorMsg("");
              } else {
                setlocalLocation({
                  city: null,
                  formatted_address: response.results[0].formatted_address,
                  lat: response.results[0].geometry.location.lat(),
                  lng: response.results[0].geometry.location.lng(),
                });
                setisAddressLoading(false);
                setisloading(false);
                seterrorMsg(res.message);
              }
            })
            .catch((error) => console.log("error " + error));
        } else {
          console.log("No result found");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };
  //handle Confirm current location
  const confirmCurrentLocation = async () => {
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }
    setisloading(true);

    fetchCity(localLocation.city, localLocation.lat, localLocation.lng)
      .then(async (result) => {
        if (result.status === 1) {
          dispatch(
            setCity({
              data: {
                id: result.data.id,
                name: localLocation.city,
                state: result.data.state,
                formatted_address: localLocation.formatted_address,
                latitude: result.data.latitude,
                longitude: result.data.longitude,
                min_amount_for_free_delivery:
                  result.data.min_amount_for_free_delivery,
                delivery_charge_method: result.data.delivery_charge_method,
                fixed_charge: result.data.fixed_charge,
                per_km_charge: result.data.per_km_charge,
                time_to_travel: result.data.time_to_travel,
                max_deliverable_distance: result.data.max_deliverable_distance,
                distance: result.data.distance,
              },
            })
          );
          fetchShop(result?.data?.latitude, result?.data?.longitude);
          setisloading(false);
          setcurrLocationClick(false);
          props.setisLocationPresent(true);
          props.setLocModal(false);
          props.bodyScroll(false);
          props.setIsAddressSelected(false);
        } else {
          seterrorMsg(result.message);
        }
      })
      .catch((error) => console.log("error ", error));
  };

  const getAddressIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "home":
        return <MdHome className="address-type-icon" />;
      case "office":
        return <MdWorkOutline className="address-type-icon" />;
      default:
        return <IoLocationOutline className="address-type-icon" />;
    }
  };

  const handleSelectAddress = (addr) => {
    dispatch(setSelectedAddress({ data: addr }));
    props.setLocModal(false);
    props.bodyScroll(false);
    props?.setisLocationPresent(true);
  };

  const fetchShop = async (latitude, longitude) => {
    try {
      const response = await api.getShop(latitude, longitude, user?.jwtToken);
      const result = await response.json();
      if (result?.status == 1) {
        dispatch(setShop({ data: result?.data }));
      }
    } catch (error) {
      console.log(error?.message);
    }
  };

  const handleCloseModal = () => {
    props.setLocModal(false);
    seterrorMsg("");
    setisloading(false);
    setcurrLocationClick(false);
    setisInputFields(false);
    setisAddressLoading(false);
    props.bodyScroll(false);
  };

  return (
    <>
      {setting.setting && (
        <>
          <div className="d-flex flex-row justify-content-between align-items-center header">
            <h5>{t("select_location")}</h5>
            <button type="button" className="" onClick={handleCloseModal}>
              <AiOutlineCloseCircle />
            </button>
          </div>

          <div className="d-flex flex-column gap-3 align-items-center body">
            {isloading ? (
              <Loader />
            ) : (
              <>
                {!currLocationClick ? (
                  <div className="location-selection-container">
                    <img
                      src={
                        setting.setting &&
                        setting?.setting?.web_settings?.web_logo
                      }
                      className="location-logo"
                      alt="location"
                    />
                    <h5>{t("select_delivery_location")}</h5>

                    {/* Location permission denied message - always show when permission is denied */}
                    {(props?.showPermissionDenied ||
                      locationPermission === "denied") && (
                      <div className="location-permission-denied">
                        <div className="location-icon-error">
                          <MdLocationOff size={24} />
                        </div>
                        <div className="location-error-content">
                          <div>
                            <h6>{t("device_location_not_enabled")}</h6>
                            <p>{t("enable_device_location")}</p>
                          </div>
                          <button
                            className="enable-location-btn"
                            onClick={requestLocationPermission}
                          >
                            {t("enable")}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Current location button */}
                    <button
                      className="current-location-btn"
                      onClick={handleLocationButtonClick}
                      disabled={isInputFields}
                      style={isInputFields ? { opacity: "0.5" } : null}
                    >
                      <BiCurrentLocation className="mx-3" />
                      {t("use_my_current_location")}
                    </button>

                    <div className="oval-continer">
                      <div className="oval">
                        <span className="separator-text">
                          <div className="or">OR</div>
                        </span>
                      </div>
                    </div>

                    <div className="input-container">
                      <StandaloneSearchBox
                        onLoad={(ref) => (inputRef.current = ref)}
                        onPlacesChanged={handlePlaceChanged}
                      >
                        <input
                          type="text"
                          id="text-places"
                          className="border-bottom"
                          placeholder={t("select_delivery_location")}
                          onFocus={() => {
                            setcurrLocationClick(false);
                            setisInputFields(true);
                          }}
                          onBlur={() => {
                            setisInputFields(false);
                          }}
                        />
                      </StandaloneSearchBox>
                    </div>

                    {/* Display saved addresses - always show if available */}
                    {addresses &&
                      addresses?.length > 0 &&
                      props.showPermissionDenied && (
                        <div className="saved-addresses-container">
                          <h6 className="saved-addresses-title">
                            {t("select_a_saved_address")}
                          </h6>
                          <div className="saved-addresses-list">
                            {addresses?.map((addr) => (
                              <div
                                key={addr.id}
                                className="saved-address-item"
                                onClick={() => handleSelectAddress(addr)}
                              >
                                <div className="address-icon-container">
                                  {getAddressIcon(addr.type)}
                                </div>
                                <div className="address-details">
                                  <span className="address-name">
                                    {addr.type}
                                  </span>
                                  <span className="address-text">
                                    {addr.address}, {addr.city}, {addr.state},{" "}
                                    {addr.country}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <>
                    <div className="w-100 mapWrapper">
                      <GoogleMap
                        streetViewControl={false}
                        tilt={true}
                        options={{
                          streetViewControl: false,
                        }}
                        zoom={11}
                        center={center}
                        mapContainerStyle={{ height: "400px" }}
                      >
                        <button
                          className="current-location-click"
                          whiletap={{ scale: 0.6 }}
                          onClick={handleCurrentLocationClick}
                        >
                          <BiCurrentLocation fill="black" className="mx-3" />
                        </button>
                        <MarkerF
                          position={center}
                          draggable={true}
                          onDragStart={onMarkerDragStart}
                          onDragEnd={onMarkerDragEnd}
                        ></MarkerF>
                      </GoogleMap>
                    </div>

                    <p className="map-content-p">
                      <b>{t("address")} : </b>
                      {isAddressLoading
                        ? "...."
                        : localLocation.formatted_address}
                    </p>

                    <div className="map-content">
                      <button
                        whiletap={{ scale: 0.6 }}
                        type="button"
                        className="btn-confirm-location"
                        onClick={confirmCurrentLocation}
                        disabled={localLocation.formatted_address === ""}
                      >
                        {t("confirm")}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Location;
